<template>
  <header :class="['header padding-y-2xl', { 'padding-top-4xl': accountView }]">
    <div
      v-if="hasLimitedOfferBadge"
      class="position-absolute"
      style="top: calc(var(--spacing-2xl) * -1); right: calc(var(--spacing-2xl) * -1); transform: rotate(15deg)"
    >
      <div class="position-relative text-center">
        <font-awesome-icon
          :icon="$v7.solid.faCertificate"
          size="7x"
          class="position-relative z-1"
          style="color: var(--fa-yellow)"
        />
        <span class="position-absolute z-2 display-block top-1 right-1 padding-lg">
          <span
            class="display-block margin-top-2xs"
            style="line-height: 1.2; font-weight: var(--font-weight-semibold)"
            >Limited Time Price!</span
          >
        </span>
      </div>
    </div>

    <font-awesome-icon
      :icon="icon"
      size="4x"
    />

    <h3
      v-if="!plan && !subscription && determinations.isBacker"
      class="margin-top-md"
    >
      <span class="display-block size-sm">Font Awesome 5</span>
      <span class="display-block size-lg">Early Backer</span>
    </h3>

    <h3
      v-if="planName"
      class="margin-y-4xs size-4xl"
    >
      {{ planName }}
      <owa-tag
        v-if="isCyclePairedMonthly"
        class="margin-left-2xs"
        label="Monthly"
        :custom-style="cycleTagStyle"
      />
      <!-- The leading space is load-bearing. Vue 3's compiler drops a
           whitespace-only node between two elements, so without it the
           heading reads "Solo MonthlyPlan" to a screen reader. -->
      <span class="sr-only"> Plan</span>
    </h3>

    <dl
      v-if="(plan || subscription) && !accountView"
      style="--list-padding-left: 0"
    >
      <dt class="sr-only">Cost:</dt>
      <dd
        class="text-center margin-bottom-0"
        style="font-weight: var(--font-weight-semibold)"
      >
        <span class="display-flex align-center">
          <span
            class="size-3xl"
            style="line-height: var(--line-height-sm)"
          >
            {{ currentDollars() }}
          </span>
          <span
            v-if="currentCents()"
            class="padding-top-4xs"
            style="align-self: flex-start; font-size: var(--font-size-lg)"
            >.{{ currentCents() }}</span
          >
        </span>
        <span class="display-block text-capitalize">{{ howLong }}</span>
      </dd>
      <dd v-if="hasAdditionalPricingDetails">
        then {{ nextYearDollars() }}<span v-if="nextYearCents()">.{{ nextYearCents() }}</span
        >/{{ periodWord }}
      </dd>
    </dl>
  </header>
</template>

<script>
  import { get } from 'lodash'
  import { mapGetters } from 'vuex'
  import { nextBilledAmount, getSubscriptionAmount, getCurrentDiscountLengthLabel, formatTotalCurrency, isProratedAmount } from 'app/views/plans/priceUtils'
  import * as customIcons from 'app/icons/custom'
  import { ANNUAL_SKU_FOR_MONTHLY } from 'app/views/plans/PlansData'

  export default {
    name: 'PlanInfo',

    props: {
      plan: {
        type: Object
      },

      subscription: {
        type: Object
      },

      determinations: {
        type: Object
      },

      price: {
        type: Number
      },

      priceWithDiscount: {
        type: Number
      },

      seats: {
        type: Number
      },

      hasLimitedOfferBadge: {
        type: Boolean,
        default: false
      },

      isCurrentPlan: {
        type: Boolean
      },

      colorSet: {
        type: Object,
        required: true
      },

      isUpgrading: {
        type: Boolean,
        default: false
      },

      planIconStyle: {
        type: String
      },

      accountView: {
        type: Boolean,
        default: false
      }
    },

    computed: {
      ...mapGetters('plans', ['subscriptionDiscounts']),

      activePlan() {
        return this.plan || get(this.subscription, ['plan'])
      },

      planName() {
        return get(this.activePlan, ['name'])
      },

      // Only tiers sold in both cycles share a name across them, so only they
      // need the cycle word. Every other plan has a single cycle and reads
      // correctly under its bare name.
      isCyclePairedMonthly() {
        return !!ANNUAL_SKU_FOR_MONTHLY[get(this.activePlan, ['sku'])]
      },

      // The card's accent scope paints --tag-background the card's own color, so
      // the pill needs an outline to read against it. The positioning reset
      // escapes `.with-top-tag .tag`, which would otherwise throw it to the top
      // of the card, and the rem font size escapes the em-based tag scale.
      cycleTagStyle() {
        return {
          'position': 'relative',
          'top': 'auto',
          'left': 'auto',
          'transform': 'none',
          'fontSize': '0.75rem',
          'verticalAlign': 'middle',
          '--tag-border-width': '2px',
          '--tag-border-style': 'solid',
          '--tag-border-color': 'var(--fa-navy)'
        }
      },

      periodWord() {
        return get(this.activePlan, ['annual'], true) ? 'year' : 'month'
      },

      hasAdditionalPricingDetails() {
        if (this.subscriptionDiscounts.length > 0) {
          if (get(this.subscriptionDiscounts[0], 'duration') === 'forever') {
            return false
          } else {
            return true
          }
        }

        return false
      },

      subscriptionDiscount() {
        return get(this, ['subscription', 'discounts', 0])
      },

      amount() {
        let val = 0

        if (this.price) {
          val = this.price
        } else if (this.isCurrentPlan) {
          val = getSubscriptionAmount(this.subscription)
        } else {
          val = get(this, ['plan', 'price', 'amount'], 0)
        }

        return val / 100
      },

      howLong() {
        let val = ''

        if (parseInt(this.amount) === 0) {
          val = 'forever'
        } else {
          val = `per ${this.periodWord}`

          if (isProratedAmount(this.subscription)) {
            val = 'prorated'
          }

          if (this.subscriptionDiscounts.length > 0) {
            val = getCurrentDiscountLengthLabel(this.subscriptionDiscounts[0], this.plan || get(this.subscription, ['plan']))
          }

          if (this.isUpgrading) {
            val = 'due today'
          }
        }

        return val
      },

      icon() {
        const { indication, isBacker } = this.determinations

        let name
        if (!this.plan && isBacker) {
          name = 'faSunrise'
        } else if (!this.plan && (indication === 'warning' || indication === 'danger')) {
          name = 'faBomb'
        } else if (this.plan && this.plan.iconName) {
          name = this.normalizeIconName(this.plan.iconName)
        } else if (this.plan && this.plan.name === 'FA-FREE') {
          name = 'faEarthAmericas'
        } else {
          name = 'faPlanetRinged'
        }

        // Legacy "+" variants (faPlanetRingedPlus, faMoonPlus, faSolarSystemPlus)
        // live in app/icons/custom under the `fac` prefix — not in any $v7 style.
        // Check custom first, then fall through to the configured style pack.
        const style = this.planIconStyle || 'solid'
        const stylePack = this.$v7[style] || {}
        const fallback = this.$v7.solid?.faPlanetRinged || null

        // eslint-disable-next-line import/namespace
        return customIcons[name] || stylePack[name] || fallback
      }
    },

    methods: {
      // Converts a stored plan iconName to the JS identifier used as a key
      // on `$v7.<style>`. New-tier factories store the identifier directly
      // (e.g. "faUser"); legacy factories store kebab-case ("moon-plus").
      // Both shapes need to land on something like "faMoonPlus" for the
      // template lookup to resolve.
      normalizeIconName(raw) {
        if (typeof raw !== 'string' || raw.length === 0) return ''
        if (raw.startsWith('fa') && /^fa[A-Z]/.test(raw)) return raw
        return (
          'fa' +
          raw
            .split('-')
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join('')
        )
      },

      currentDollars() {
        return formatTotalCurrency(this.amount)[0]
      },

      currentCents() {
        return formatTotalCurrency(this.amount)[1]
      },

      nextYearDollars() {
        return nextBilledAmount(this.subscriptionDiscounts[0], this.amount, this.plan, this.seats)[0]
      },

      nextYearCents() {
        return nextBilledAmount(this.subscriptionDiscounts[0], this.amount, this.plan, this.seats)[1]
      }
    }
  }
</script>
